import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateTime } from 'luxon';

import { toIsoDate } from 'src/shared/util/secondary-page-context';

import type { FinancialTransaction } from 'src/shared/services/property-transactions.service';
import type { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

/** How long a customer spreads their own transaction: one day, a whole number of months, or up to a day they pick. */
export type SpreadChoice = 'none' | '1' | '6' | '12' | '36' | 'custom';

const PRESET_MONTHS = [1, 6, 12, 36];

export const SPREAD_ITEMS: SelectModel[] = [
  { value: 'none', viewValue: 'No spread (one day)' },
  { value: '1', viewValue: '1 month' },
  { value: '6', viewValue: '6 months' },
  { value: '12', viewValue: '12 months' },
  { value: '36', viewValue: '36 months' },
  { value: 'custom', viewValue: 'Until a date I pick' },
];

type PeriodRow = Pick<FinancialTransaction, 'period_start_date' | 'period_end_date' | 'source_record_type'>;
type PaymentRow = Pick<FinancialTransaction, 'transacted_on' | 'transacted_at_origin' | 'source_record_type'>;

const ISO = 'yyyy-MM-dd';

function day(value: string | null | undefined): DateTime | null {
  const parsed = value ? DateTime.fromISO(value) : null;
  return parsed?.isValid ? parsed : null;
}

/** The day before the same day N months later: from 3 March, six months ends 2 September. */
export function endAfterMonths(start: string, months: number): string | null {
  const first = day(start);
  return first ? first.plus({ months }).minus({ days: 1 }).toFormat(ISO) : null;
}

/** The last day a choice covers. Null while a custom end has not been picked. */
export function spreadEndDate(start: string, choice: SpreadChoice, customEnd: string | null): string | null {
  if (choice === 'none') { return day(start) ? start : null; }
  if (choice === 'custom') { return day(customEnd) ? customEnd : null; }
  return endAfterMonths(start, Number(choice));
}

/** Reads a saved period back as the choice that makes it, so the form opens where the customer left it. */
export function spreadChoiceOf(start: string, end: string | null | undefined): SpreadChoice {
  if (!end || end === start) { return 'none'; }
  const preset = PRESET_MONTHS.find(months => endAfterMonths(start, months) === end);
  return preset ? String(preset) as SpreadChoice : 'custom';
}

/** "Mar 3 – Sep 2, 2026", "Dec 3, 2025 – Jun 2, 2026", or "Mar 3, 2026" for one day. */
export function rangeLabel(start: string | null | undefined, end: string | null | undefined): string {
  const first = day(start);
  if (!first) { return ''; }
  const last = day(end);
  if (!last || last <= first) { return first.toFormat('MMM d, yyyy'); }
  const firstFormat = first.year === last.year ? 'MMM d' : 'MMM d, yyyy';
  return `${first.toFormat(firstFormat)} – ${last.toFormat('MMM d, yyyy')}`;
}

/**
 * The days a row belongs to. A stay's period ends on its last night, but stays read
 * check-in to check-out everywhere else (its reservation included), so it shows check-out.
 */
export function periodLabel(txn: PeriodRow): string {
  const isStay = txn.source_record_type === 'GuestReservation';
  const end = isStay && txn.period_end_date && txn.period_end_date !== txn.period_start_date
    ? day(txn.period_end_date)?.plus({ days: 1 }).toFormat(ISO)
    : txn.period_end_date;
  return rangeLabel(txn.period_start_date, end);
}

/** Why the paid day is what it is, when TIDY or the customer chose it rather than a source. */
export function paidOnNote(txn: PaymentRow): string {
  if (txn.transacted_at_origin === 'customer') { return 'set by you'; }
  if (txn.transacted_at_origin !== 'guess') { return ''; }
  return txn.source_record_type === 'GuestReservation' ? 'estimated from check-in' : 'estimated from the date';
}

/** "Paid Aug 29, 2026 (estimated from check-in)", or "Not paid yet". `short` trims the note to "(estimated)". */
export function paidOnLabel(txn: PaymentRow, short = false): string {
  const paid = day(txn.transacted_on);
  if (!paid) { return 'Not paid yet'; }
  const note = paidOnNote(txn);
  const shortNote = txn.transacted_at_origin === 'guess' ? 'estimated' : note;
  const shown = short ? shortNote : note;
  return `Paid ${paid.toFormat('MMM d, yyyy')}${shown ? ` (${shown})` : ''}`;
}

/**
 * A custom spread needs a last day, on or after the first. Reads its siblings `date` and
 * `spread`, so revalidate it when either changes.
 */
export const spreadEndValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const group = control.parent;
  if (!group || group.get('spread')?.value !== 'custom') { return null; }
  const end = toIsoDate(control.value);
  const start = toIsoDate(group.get('date')?.value);
  if (!end) { return { required: true }; }
  return start && end < start ? { beforeStart: true } : null;
};

/** The line under the Spread field: how each reading counts the period just picked. */
export function spreadSummary(start: string | null, end: string | null): string {
  if (!start || !end || end === start) { return ''; }
  return `Accrual spreads it over ${rangeLabel(start, end)}. Cash counts it whole in the month it was paid.`;
}
